export enum Pages {
  root = '/',

  // Auth related routes, all located in /(auth)/
  forgotPassword = '/forgot-password',
  signin = '/sign-in',
  signup = '/sign-up',
  verify = '/verify',
  resendVerification = '/resend-verification',
  forbidden = '/403',

  // General (public) pages
  // (sometimes being linked to from the app)
  WaitList = '/waitlist',
  about = '/about',
  contact = '/contact',
  imprint = '/imprint',
  privacyPolicy = '/privacy-policy',
  termsOfUse = '/terms-of-use',

  // Registered user routes, all located in /u/
  logout = '/u/logout',

  /**
   * User dashboard
   */
  Home = '/u/home',
  Bookmarks = '/u/bookmarks',

  // Settings
  Settings = '/u/settings',
  SettingsPreferences = '/u/settings/preferences',
  SettingsApiKeys = '/u/settings/api-keys',
  SettingsAccount = '/u/settings/account',
  SettingsPublicProfile = '/u/settings/public-profile',
  SettingsSiteSettings = '/u/settings/site-settings',
  SettingsNotifications = '/u/settings/notifications',
  SettingsPassword = '/u/settings/password',
  SettingsDev = '/u/settings/dev',

  // Goals
  Goals = '/u/goals',

  // Notes
  NotesNew = '/u/notes/new',
  NotesList = '/u/notes',
  NotesView = '/u/notes/:id',
  NotesEdit = '/u/notes/:id/edit',

  // Sessions
  SessionsList = '/u/sessions',
  SessionsNew = '/u/sessions/new',
  SessionsView = '/u/sessions/:id',
  SessionsEdit = '/u/sessions/:id/edit',
  SessionsFocus = '/u/sessions/:id/focus/:activityId',

  // Training Plans
  TrainingPlansList = '/u/training-plans',
  TrainingPlansNew = '/u/training-plans/new',
  TrainingPlansView = '/u/training-plans/:id',
  TrainingPlansEdit = '/u/training-plans/:id/edit',
  WeeklyPlansList = '/u/training-plans/weekly',
  WeeklyPlansNew = '/u/training-plans/weekly/new',
  WeeklyPlansView = '/u/training-plans/weekly/:id',
  WeeklyPlansEdit = '/u/training-plans/weekly/:id/edit',

  // Templates
  TemplatesList = '/u/templates',
  TemplateNew = '/u/templates/new',
  TemplateEdit = '/u/templates/edit/:template_id',
  TemplateDelete = '/u/templates/delete/:template_id',
  TemplateView = '/u/templates/:template_id',

  // Distance Planning
  LengthControl = '/u/length-control',
  LengthControlCheatSheet = '/u/length-control/cheat-sheet',

  // Club Distances
  ClubLengthControl = '/u/club-length-control',

  // What's In My Bag
  WhatsInMyBag = '/u/whats-in-my-bag',

  // Activities
  ActivityTrack = '/u/activities/track',

  // Habits
  Habits = '/u/habits',
  HabitsManage = '/u/habits/manage',
  HabitsGoals = '/u/habits/goals',
  HabitsDashboard = '/u/habits/dashboard',

  // Reports
  Reports = '/u/reports',
  WeeklyReport = '/u/reports/weekly',
  TrainingDistribution = '/u/reports/training-distribution',

  // Equipment
  Equipment = '/u/equipment',

  // Drills
  Drills = '/u/drills',
  DrillDetail = '/u/drills/:id',

  // Contacts
  Contacts = '/u/contacts',
  ContactDetail = '/u/contacts/:id',

  // Tags
  Tags = '/u/tags',
  TagsList = '/u/tags/list',

  // Dev
  Dev = '/dev',
  DevToasts = '/dev/toasts',
}

export enum AdminPages {
  Admin = '/admin',

  ApiDocs = '/admin/api-docs',
  ApiKeys = '/admin/api-keys',
  ErrorLogs = '/admin/error-logs',
  ErrorLogDetail = '/admin/error-logs/:id',

  Sandbox = '/admin/sandbox',
  Links = '/admin/links',
  Dbml = '/admin/dbml',
  Users = '/admin/users',
  SandboxMarkdownEditor = '/admin/sandbox/markdown-editor',
  SandboxSpeechInput = '/admin/sandbox/speech-input',
  SandboxTextInput = '/admin/sandbox/text-input',
  SandboxVideoCaptureTest = '/admin/sandbox/video-capture-test',
  SeedTemplates = '/admin/seeds/templates',
  SeedNotes = '/admin/seeds/notes',

  TrainingAids = '/admin/training-aids',
  TrainingAidDetail = '/admin/training-aids/:id',
  TrainingAidNew = '/admin/training-aids/new',
  Translations = '/admin/translations',

  Drills = '/admin/drills',
  DrillDetail = '/admin/drills/:id',
  DrillNew = '/admin/drills/new',
  GolfClubs = '/admin/golf-clubs',
  GolfClubDetail = '/admin/golf-clubs/:id',
  GolfClubNew = '/admin/golf-clubs/new',
  Storage = '/admin/storage',
  DrillResearcher = '/admin/drill-researcher',
  GolfCourses = '/admin/golf-courses',
  GolfCourseDetail = '/admin/golf-courses/:courseId',
  GolfCourseNewVersion = '/admin/golf-courses/:courseId/versions/new',

  ActivityCategories = '/admin/activity-categories',
}

export enum PageCat {
  user,
  public,
  // Development tools (should never be shown in production)
  dev,
  admin,
}

type AllPages = Pages | AdminPages;

type AppLink = {
  key: AllPages;
  category: PageCat;
  label: string;
  path: string;
  enabled: boolean;
};

/**
 * Generates a URL based on the provided key and optional query parameters.
 *
 * @param {Pages} key - The key representing the desired page.
 * @param {Record<string, string>} [params] - An optional object containing key-value pairs to be added as query parameters.
 * @returns {string} - The generated URL with optional query parameters.
 *
 * @example
 * // Example 1: Adding one search parameter
 * const urlWithOneParam = getLink(PageEnum.login, { ref: 'newsletter' });
 * console.log(urlWithOneParam); // Output: /login?ref=newsletter
 *
 * @example
 * // Example 2: Adding two search parameters
 * const urlWithTwoParams = getLink(PageEnum.register, { ref: 'newsletter', campaign: 'summer2023' });
 * console.log(urlWithTwoParams); // Output: /register?ref=newsletter&campaign=summer2023
 *
 * @example
 * // Example 3: Using getLink within Html
 * <a href="{getLink(PageEnum.login)}">Login</a>
 */
export const getLink = (key: AllPages, params?: Record<string, string>, throwErr = false): string => {
  const link = appLinks.find((link) => link.key === key);
  let url = '';
  if (link) {
    url = link.path;
  }
  // Simplified check for all page enums
  if (url === '') {
    url = key.toString();
  }

  if (url !== '' && params) {
    const queryString = new URLSearchParams(params).toString();
    url += `?${queryString}`;
  }

  if (url === '' && throwErr) {
    // Last fallback, so really nothing has worked
    console.error('No link found for key:', key);
    url = '/';
  }

  return url;
};

// Note: Order the links by the PageEnum
export const appLinks: AppLink[] = [
  {
    category: PageCat.public,
    key: Pages.about,
    label: 'About',
    path: Pages.about.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.contact,
    label: 'Contact',
    path: Pages.contact.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.WaitList,
    label: 'Waitlist',
    path: Pages.WaitList.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.forgotPassword,
    label: 'Forgot Password',
    path: Pages.forgotPassword.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.signin,
    label: 'Login',
    path: Pages.signin.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.signup,
    label: 'Register',
    path: Pages.signup.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.verify,
    label: 'Verify',
    path: Pages.verify.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.resendVerification,
    label: 'Resend Verification',
    path: Pages.resendVerification.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.root,
    label: 'Home',
    path: Pages.root.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.imprint,
    label: 'Imprint',
    path: Pages.imprint.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.privacyPolicy,
    label: 'Privacy Policy',
    path: Pages.privacyPolicy.toString(),
    enabled: true,
  },
  {
    category: PageCat.public,
    key: Pages.termsOfUse,
    label: 'Terms of Use',
    path: Pages.termsOfUse.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.logout,
    label: 'Logout',
    path: Pages.logout.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Home,
    label: 'Dashboard',
    path: Pages.Home.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Bookmarks,
    label: 'Bookmarks',
    path: Pages.Bookmarks.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.SessionsList,
    label: 'Sessions',
    path: Pages.SessionsList.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Settings,
    label: 'Settings',
    path: Pages.Settings.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.SettingsPreferences,
    label: 'Settings - Preferences',
    path: Pages.SettingsPreferences.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.SettingsAccount,
    label: 'Settings - Account',
    path: Pages.SettingsAccount.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.SettingsApiKeys,
    label: 'Settings - API Keys',
    path: Pages.SettingsApiKeys.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Contacts,
    label: 'Contacts',
    path: Pages.Contacts.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Tags,
    label: 'Tags',
    path: Pages.Tags.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Goals,
    label: 'Goals',
    path: Pages.Goals.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.LengthControl,
    label: 'Distance Planning',
    path: Pages.LengthControl.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.LengthControlCheatSheet,
    label: 'Distance Planning - Cheat Sheet',
    path: Pages.LengthControlCheatSheet.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.ClubLengthControl,
    label: 'Club Distances',
    path: Pages.ClubLengthControl.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.WhatsInMyBag,
    label: "What's In My Bag",
    path: Pages.WhatsInMyBag.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.NotesList,
    label: 'Notes',
    path: Pages.NotesList.toString(),
    enabled: true,
  },
  {
    category: PageCat.dev,
    key: Pages.Dev,
    label: 'Dev Tools',
    path: Pages.Dev.toString(),
    enabled: true,
  },
  {
    category: PageCat.dev,
    key: Pages.DevToasts,
    label: 'Toasts',
    path: Pages.DevToasts.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.Admin,
    label: 'Dashboard',
    path: AdminPages.Admin.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.ApiDocs,
    label: 'API Docs',
    path: AdminPages.ApiDocs.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.ApiKeys,
    label: 'API Keys',
    path: AdminPages.ApiKeys.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.Sandbox,
    label: 'Sandbox',
    path: AdminPages.Sandbox.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.SandboxMarkdownEditor,
    label: 'Markdown Editor',
    path: AdminPages.SandboxMarkdownEditor.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.SandboxSpeechInput,
    label: 'Speech Input',
    path: AdminPages.SandboxSpeechInput.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.SandboxTextInput,
    label: 'Text Input',
    path: AdminPages.SandboxTextInput.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.SandboxVideoCaptureTest,
    label: 'Video Capture Test',
    path: AdminPages.SandboxVideoCaptureTest.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.Users,
    label: 'Users',
    path: AdminPages.Users.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfClubs,
    label: 'Golf Clubs',
    path: AdminPages.GolfClubs.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfClubDetail,
    label: 'Golf Club Detail',
    path: AdminPages.GolfClubDetail.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfClubNew,
    label: 'New Golf Club',
    path: AdminPages.GolfClubNew.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.DrillResearcher,
    label: 'Drill Researcher',
    path: AdminPages.DrillResearcher.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfCourses,
    label: 'Golf Courses',
    path: AdminPages.GolfCourses.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfCourseDetail,
    label: 'Golf Course Detail',
    path: AdminPages.GolfCourseDetail.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.GolfCourseNewVersion,
    label: 'New Golf Course Version',
    path: AdminPages.GolfCourseNewVersion.toString(),
    enabled: true,
  },
  {
    category: PageCat.admin,
    key: AdminPages.ActivityCategories,
    label: 'Activity Categories',
    path: AdminPages.ActivityCategories.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.TemplatesList,
    label: 'Templates',
    path: Pages.TemplatesList.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.TrainingPlansList,
    label: 'Training Plans',
    path: Pages.TrainingPlansList.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.WeeklyPlansList,
    label: 'Weekly Plans',
    path: Pages.WeeklyPlansList.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Habits,
    label: 'Habits',
    path: Pages.Habits.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.Reports,
    label: 'Reports',
    path: Pages.Reports.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.WeeklyReport,
    label: 'Weekly Report',
    path: Pages.WeeklyReport.toString(),
    enabled: true,
  },
  {
    category: PageCat.user,
    key: Pages.TrainingDistribution,
    label: 'Training Distribution',
    path: Pages.TrainingDistribution.toString(),
    enabled: true,
  },
];
